// Cầu nối Bài giảng → Phiên trực tiếp: cung cấp LessonDoc qua context (builder preview
// + trang xem bài) và chuyển các câu hỏi tương tác THẬT của bài thành câu hỏi phiên live.
import React, { createContext, useContext } from 'react'
import type { LessonDoc } from '../types/LessonDoc'

const LessonDocContext = createContext<LessonDoc | null>(null)

export const LessonDocProvider: React.FC<{ lesson: LessonDoc | null; children: React.ReactNode }> = ({ lesson, children }) => (
  <LessonDocContext.Provider value={lesson}>{children}</LessonDocContext.Provider>
)

export const useLessonDoc = () => useContext(LessonDocContext)

// Câu hỏi theo định dạng phiên trực tiếp (TeacherSession)
export interface LiveQuestion {
  id: string | number
  type: 'word_cloud' | 'mcq' | 'short_answer'
  text: string
  options?: string[]
  correctAnswer?: string
  image?: string
}

/** Trích TOÀN BỘ câu hỏi tương tác thật trong bài giảng → câu hỏi phiên live.
 * Hỗ trợ: interactive_question (1 đáp án), multiple_response (nhiều đáp án),
 * quiz (multiple_choice / true_false / fill_blank). sequence bỏ qua (live không hỗ trợ kéo thả). */
export function lessonToLiveQuestions(doc: LessonDoc | null): LiveQuestion[] {
  if (!doc) return []
  const out: LiveQuestion[] = []
  let n = 0
  const push = (q: LiveQuestion) => { out.push(q); n++ }

  for (const sec of doc.sections || []) {
    for (const card of sec.cards || []) {
      // Câu hỏi nằm trong rich card (nội dung AI sinh / soạn tay)
      if (card.type === 'rich' && Array.isArray((card as any).items)) {
        for (const it of (card as any).items) {
          if (!it || typeof it !== 'object') continue
          if (it.type === 'interactive_question' && it.question && Array.isArray(it.options)) {
            const ci = Number(it.correctAnswer)
            push({
              id: `live-${n}`, type: 'mcq', text: it.question,
              options: it.options.map(String),
              correctAnswer: Number.isInteger(ci) && it.options[ci] != null ? String(it.options[ci]) : undefined,
              image: it.image || undefined,
            })
          } else if (it.type === 'multiple_response_question' && it.question && Array.isArray(it.options)) {
            const corr = (Array.isArray(it.correctAnswers) ? it.correctAnswers : [])
              .map((i: any) => it.options[Number(i)]).filter((x: any) => x != null).map(String)
            push({
              id: `live-${n}`, type: 'mcq', text: it.question,
              options: it.options.map(String),
              correctAnswer: corr.length ? corr.join(', ') : undefined,
            })
          } else if (it.type === 'quiz' && Array.isArray(it.questions)) {
            for (const q of it.questions) pushQuizQ(q, push, n)
          }
        }
      }
      // Quiz card độc lập
      if (card.type === 'quiz' && Array.isArray((card as any).questions)) {
        for (const q of (card as any).questions) pushQuizQ(q, push, n)
      }
    }
  }
  return out
}

function pushQuizQ(q: any, push: (x: LiveQuestion) => void, n: number) {
  if (!q || !q.question) return
  if (q.type === 'multiple_choice' && Array.isArray(q.options)) {
    const ci = Number(q.correct)
    push({
      id: `live-q-${n}-${Math.abs(JSON.stringify(q.question).length)}`, type: 'mcq', text: q.question,
      options: q.options.map(String),
      correctAnswer: Number.isInteger(ci) && q.options[ci] != null ? String(q.options[ci]) : undefined,
    })
  } else if (q.type === 'true_false') {
    push({
      id: `live-q-${n}-tf`, type: 'mcq', text: q.question,
      options: ['Đúng', 'Sai'],
      correctAnswer: q.correct === true ? 'Đúng' : q.correct === false ? 'Sai' : undefined,
    })
  } else if (q.type === 'fill_blank' && q.answer) {
    push({ id: `live-q-${n}-fb`, type: 'short_answer', text: q.question, correctAnswer: String(q.answer) })
  }
}
